"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { apiFetch, errorDetail } from "@/lib/api";
import { getTheme, type Theme } from "@/lib/theme";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

// After this many CONSECUTIVE failed sign-ins, offer the reset link inline.
// Purely a UX nudge — the security throttles live in the backend and are not
// influenced by anything on this page.
const RESET_PROMPT_AFTER_FAILURES = 3;

// sessionStorage, not component state, and deliberately not localStorage:
//   * component state alone loses the tally on a page reload, and reloading is
//     exactly what people do when a sign-in will not take — the user most in
//     need of the prompt would be the one least likely to see it;
//   * sessionStorage is per-tab and dies with the tab, so the prompt reflects
//     "you are struggling right now" rather than something that happened last
//     week. localStorage would greet a returning user with "Trouble signing
//     in?" before they had typed anything.
// It holds no security decision, so its being user-writable costs nothing.
const FAIL_COUNT_KEY = "login_failed_attempts";

// The tally is stored WITH the address it belongs to, and a different address
// starts over (Codex QA). On a shared machine — a lab bench, a warehouse
// terminal — the next person would otherwise walk up to someone else's
// "Trouble signing in?" prompt before typing anything. It also makes
// "consecutive" mean what it says: three failures on your own address, not
// three failures spread across three different people's.
type FailureTally = { email: string; count: number };

function readTally(): FailureTally {
  const empty: FailureTally = { email: "", count: 0 };
  if (typeof window === "undefined") return empty;
  try {
    const raw = window.sessionStorage.getItem(FAIL_COUNT_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<FailureTally>;
    if (typeof parsed?.email !== "string" || typeof parsed?.count !== "number") return empty;
    return { email: parsed.email, count: parsed.count > 0 ? parsed.count : 0 };
  } catch {
    // Hand-edited or stale-format value — a UI hint is never worth throwing on.
    return empty;
  }
}

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [theme, setTheme] = useState<Theme | null>(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  // Which address the tally belongs to, so the prompt can be shown only to the
  // person it is about (see readTally).
  const [tallyEmail, setTallyEmail] = useState("");
  // /reset-password sends the user here with ?reset=1 after a successful
  // change. The toast it fires survives the client-side navigation, but a
  // toast is gone in four seconds and this is the moment the user needs to be
  // sure the change took — so the confirmation is also on the card.
  const [justReset, setJustReset] = useState(false);

  useEffect(() => {
    setTheme(getTheme(window.location.hostname));
    setJustReset(new URLSearchParams(window.location.search).get("reset") === "1");
    // Read in an effect, never during render: sessionStorage does not exist on
    // the server, and seeding state from it directly would hydrate-mismatch.
    const tally = readTally();
    setFailedAttempts(tally.count);
    setTallyEmail(tally.email);
  }, []);

  function recordFailure(attemptedEmail: string) {
    const previous = readTally();
    const normalized = attemptedEmail.trim().toLowerCase();
    const next = previous.email === normalized ? previous.count + 1 : 1;
    window.sessionStorage.setItem(
      FAIL_COUNT_KEY, JSON.stringify({ email: normalized, count: next }),
    );
    setFailedAttempts(next);
    setTallyEmail(normalized);
  }

  function clearFailures() {
    window.sessionStorage.removeItem(FAIL_COUNT_KEY);
    setFailedAttempts(0);
    setTallyEmail("");
  }

  // Shown only once the CURRENTLY typed address is the one that has been
  // failing. A restored tally therefore stays invisible until its owner types
  // their address again — the next person at a shared terminal opens a clean
  // page, and nobody is told about attempts that were not theirs.
  const showResetPrompt =
    failedAttempts >= RESET_PROMPT_AFTER_FAILURES &&
    email.trim().toLowerCase() === tallyEmail;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await apiFetch<{
        access_token: string;
        refresh_token: string;
      }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      // Consecutive, so any success wipes the tally.
      clearFailures();

      // Fetch user profile info for sidebar/header
      let role: string | null = null;
      let mustChangePassword = false;
      try {
        const me = await apiFetch<{
          full_name: string;
          email: string;
          role: string;
          must_change_password?: boolean;
        }>("/auth/me");
        localStorage.setItem("user_name", me.full_name);
        localStorage.setItem("user_email", me.email);
        localStorage.setItem("user_role", me.role);
        role = me.role;
        mustChangePassword = me.must_change_password === true;
      } catch {}

      // An account on an admin-issued temporary password gets exactly one
      // destination — the backend refuses every other route until the user
      // picks their own password, so any other redirect would land on errors.
      if (mustChangePassword) {
        router.push("/change-password");
        return;
      }

      // Both client-tier roles land on the portal Home; everyone else on /dashboard.
      if (role === "client" || role === "client_admin") {
        router.push("/portal");
      } else {
        router.push("/dashboard");
      }
    } catch (err) {
      // errorDetail, not err.message: apiFetch throws the RAW response body,
      // so a failed sign-in used to put `{"detail":"Invalid email or password"}`
      // — braces and all — in the toast. Noticed while driving the
      // reset-then-sign-in flow, which ends on exactly this screen.
      toast.error(errorDetail(err, "Invalid credentials"));
      // Counted on ANY failed sign-in, including a 429/503, because the user's
      // experience is the same either way: it did not work and they may want
      // the reset link. The backend does not tell us which failure this was —
      // that uniformity is deliberate (see INVALID_CREDENTIALS_DETAIL) — so
      // this cannot and must not try to distinguish them.
      recordFailure(email);
    } finally {
      setLoading(false);
    }
  }

  if (!theme) return null;

  return (
    <Card className="border-0 shadow-xl shadow-slate-200/50">
      <CardHeader className="space-y-4 pb-2 pt-8 px-8">
        <div className="flex flex-col items-center gap-3">
          <Image
            src={theme.logo}
            alt={theme.name}
            width={125}
            height={125}
            className="h-[125px] w-[125px]"
          />
          <div className="text-center">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              {theme.name}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {theme.tagline}
            </p>
          </div>
        </div>
        <Separator />
      </CardHeader>

      <CardContent className="px-8 pb-8 pt-4">
        {justReset && (
          <p
            className="mb-5 rounded-md bg-secondary px-3 py-2 text-sm text-secondary-foreground"
            role="status"
          >
            Your password has been reset. Sign in with your new password.
          </p>
        )}

        {/* Third consecutive failure → offer the reset path, without taking
            anything away. The user stays on /login and the form below is
            untouched and fully usable: the most likely next event is that they
            get it right on the fourth try, and a redirect (or anything that
            covered the fields) would steal the page from someone who was one
            keystroke from being done. This only ADDS an option. */}
        {showResetPrompt && (
          <div
            role="status"
            aria-live="polite"
            className="mb-5 flex items-start gap-3 rounded-md border border-primary bg-primary/5 px-4 py-3"
          >
            <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            <div className="space-y-0.5">
              <p className="text-sm font-medium text-foreground">
                Trouble signing in?
              </p>
              <Link
                href="/forgot-password"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
              >
                Reset your password <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <Link
                href="/forgot-password"
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="h-10"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-10 font-medium"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              "Sign In"
            )}
          </Button>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Protected by Taiyo Intelligence Engine
        </p>
      </CardContent>
    </Card>
  );
}
